import { gql, type TypedDocumentNode } from '@apollo/client';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { WidgetChip } from '@sorare/core/src/atoms/ui/Chip/WidgetChip/index.tsx';
import { ANY_SPORT_PLAY_MISSIONS_CATEGORY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import { DumbFeaturedCollectionTaskWidget } from '#src/components/missions/DumbFeaturedCollectionTaskWidget/index.tsx';

import type { ProFeaturedCollectionTaskWidget_currentUser } from './__generated__/index.graphql.ts';

type Props = {
  rarity: Rarity;
  currentUser?: ProFeaturedCollectionTaskWidget_currentUser | null;
};

export const ProFeaturedCollectionTaskWidget = ({
  rarity,
  currentUser,
}: Props) => {
  const { sport, generateSportContextPath } = useSportContext();
  const {
    flags: {
      enableSorare27Nav = { BASEBALL: false, FOOTBALL: false, NBA: false },
    },
  } = useFeatureFlags();

  if (!enableSorare27Nav[sport]) {
    return null;
  }

  const hasUnclaimedTasks =
    (currentUser?.myCollectUnclaimedTasksCount ?? 0) > 0;

  return (
    <DumbFeaturedCollectionTaskWidget
      chip={hasUnclaimedTasks ? <WidgetChip state="reward" /> : undefined}
      size={5}
      progresses={[1]}
      to={generateSportContextPath(ANY_SPORT_PLAY_MISSIONS_CATEGORY_RARITY, {
        params: { category: 'collect', rarity },
      })}
    />
  );
};

ProFeaturedCollectionTaskWidget.fragments = {
  currentUser: gql`
    fragment ProFeaturedCollectionTaskWidget_currentUser on CurrentUser {
      slug
      myCollectUnclaimedTasksCount: myUnclaimedTasksCount(
        sport: $sport
        group: "collect"
      )
    }
  ` as TypedDocumentNode<ProFeaturedCollectionTaskWidget_currentUser>,
};
